"use client";

// React Imports
import { useRef, useState } from "react";

// Next Imports
import { useParams, usePathname, useRouter } from "next/navigation";

// Third Party Imports
import { setCookie } from "cookies-next/client";

// MUI Imports
import IconButton from "@mui/material/IconButton";
import Popper from "@mui/material/Popper";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";

// Type Imports
import type { Locale } from "@configs/i18n";
import type { getDictionary } from "@/utils/getDictionary";

// Hook Imports
import { useSettings } from "@core/hooks/useSettings";

type LanguageDataType = {
  langCode: Locale;
  langName: string;
};

const getLocalePath = (pathName: string, locale: string) => {
  if (!pathName) return "/";
  const segments = pathName.split("/");

  segments[1] = locale;

  return segments.join("/");
};

const LanguageDropdown = ({ dictionary }: { dictionary: Awaited<ReturnType<typeof getDictionary>> }) => {
  // States
  const [open, setOpen] = useState(false);

  // Refs
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Hooks
  const pathName = usePathname();
  const { settings } = useSettings();
  const { lang } = useParams();
  const router = useRouter();

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const languageData: LanguageDataType[] = [
    {
      langCode: "en",
      langName: dictionary["lang"].english,
    },
    {
      langCode: "nl",
      langName: dictionary["lang"].dutch,
    },
  ];

  return (
    <>
      <IconButton ref={anchorRef} onClick={handleToggle} className='text-textPrimary'>
        <i className='ri-translate-2' />
      </IconButton>
      <Popper open={open} transition disablePortal placement='bottom-start' anchorEl={anchorRef.current} className='min-is-[160px] !mbs-4 z-[1]'>
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom-start" ? "left top" : "right top",
            }}>
            <Paper className={settings.skin === "bordered" ? "border shadow-none" : "shadow-lg"}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList onKeyDown={handleClose}>
                  {languageData.map(locale => (
                    <MenuItem
                      key={locale.langCode}
                      onClick={() => {
                        setCookie("NEXT_LOCALE", locale.langCode, { path: '/' });
                        router.push(getLocalePath(pathName, locale.langCode));
                        handleClose();
                      }}
                      selected={lang === locale.langCode}
                      className='pli-4'>
                      {locale.langName}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default LanguageDropdown;
